import React, { Component } from "react";

function parseValue(endValue) {
  if (typeof endValue === "number") return { prefix: "", numeric: endValue, suffix: "" };
  const match = String(endValue).match(/^([^\d]*)(\d[\d,.]*)([^\d]*)$/);
  if (match) {
    return {
      prefix: match[1],
      numeric: parseFloat(match[2].replace(/,/g, "")),
      suffix: match[3],
    };
  }
  // No numeric portion — treat as static label
  return { prefix: endValue, numeric: null, suffix: "" };
}

class AnimatedCounter extends Component {
  constructor(props) {
    super(props);
    const { prefix, numeric, suffix } = parseValue(props.endValue);
    this.prefix = prefix;
    this.numericEnd = numeric;
    this.suffix = suffix;
    this.state = {
      currentValue: 0,
      hasAnimated: false,
    };
    this.counterRef = React.createRef();
    this.observer = null;
  }

  componentDidMount() {
    const { animateOnView = true } = this.props;

    if (animateOnView) {
      this.setupIntersectionObserver();
    } else {
      this.startAnimation();
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }

  setupIntersectionObserver = () => {
    const options = {
      threshold: 0.3, // Trigger when 30% of component is visible
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !this.state.hasAnimated) {
          this.startAnimation();
          this.setState({ hasAnimated: true });
        }
      });
    }, options);

    if (this.counterRef.current) {
      this.observer.observe(this.counterRef.current);
    }
  };

  startAnimation = () => {
    const {
      duration = 2000,
      easing = "easeOutQuart",
    } = this.props;

    if (this.numericEnd === null) return; // static string, nothing to animate

    const startTime = Date.now();

    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easedProgress = this.applyEasing(progress, easing);
      const currentValue = Math.floor(this.numericEnd * easedProgress);

      this.setState({ currentValue });

      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(animate);
      } else {
        this.setState({ currentValue: this.numericEnd });
      }
    };

    this.animationFrame = requestAnimationFrame(animate);
  };

  applyEasing = (t, easing) => {
    const easingFunctions = {
      linear: (t) => t,
      easeInQuad: (t) => t * t,
      easeOutQuad: (t) => t * (2 - t),
      easeInOutQuad: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
      easeOutQuart: (t) => 1 - Math.pow(1 - t, 4),
      easeOutExpo: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
    };

    return easingFunctions[easing] ? easingFunctions[easing](t) : t;
  };

  formatNumber = (num) => {
    const { useCommas = true } = this.props;
    return useCommas ? num.toLocaleString() : num;
  };

  render() {
    const { label, fontSize = "50px", labelSize = "18px", style = {} } = this.props;
    const { currentValue } = this.state;

    return (
      <div
        ref={this.counterRef}
        style={{
          textAlign: "center",
          padding: "20px",
          ...style,
        }}
      >
        <div
          style={{
            fontSize: fontSize,
            fontWeight: "300",
            lineHeight: "1.2",
            marginBottom: "8px",
          }}
        >
          {this.numericEnd === null
          ? this.prefix
          : `${this.prefix}${this.formatNumber(currentValue)}${this.suffix}`}
        </div>
        {label && (
          <div
            style={{
              fontSize: labelSize,
              fontWeight: "400",
              color: "#333",
            }}
          >
            {label}
          </div>
        )}
      </div>
    );
  }
}

export default AnimatedCounter;